import React, { useState, useEffect } from 'react';
import { useCookieConsent } from '../hooks/useCookieConsent';
import { COOKIE_CATEGORIES } from '../utils/cookieConstants';
import { X } from 'lucide-react';

const CookieSettingsModal = ({ isOpen, onClose }) => {
  const { consent, saveSettings } = useCookieConsent();
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: true,
  });

  useEffect(() => {
    if (isOpen) {
      setPreferences({
        necessary: true,
        analytics: consent ? Boolean(consent.analytics) : true,
      });
    }
  }, [isOpen, consent]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleToggle = (categoryId) => {
    if (categoryId === 'necessary') return;
    setPreferences((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleSave = () => {
    saveSettings(preferences);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 transition-opacity"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
    >
      <div className="bg-white w-full max-w-xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-gray-100">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 id="cookie-modal-title" className="text-lg font-bold text-gray-900">
            Cookie設定
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="閉じる"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto space-y-6">
          <p className="text-sm text-gray-600 leading-relaxed">
            Billwaveでは、サービスの品質向上および分析のためにCookieを使用しています。カテゴリのトグルを操作することで、設定をカスタマイズできます。
          </p>

          <div className="space-y-4">
            {COOKIE_CATEGORIES.map((cat) => {
              const isChecked = Boolean(preferences[cat.id]);
              const isDisabled = cat.required;

              return (
                <div
                  key={cat.id}
                  className="p-4 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-gray-900">
                        {cat.name}
                      </span>
                      {cat.required && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                          常に有効
                        </span>
                      )}
                    </div>

                    {/* Toggle Switch */}
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={() => handleToggle(cat.id)}
                        aria-label={`${cat.name}の有効化設定`}
                      />
                      <div
                        className={`w-11 h-6 rounded-full peer transition-colors ${
                          isDisabled ? 'opacity-60 cursor-not-allowed' : ''
                        } ${
                          isChecked ? 'bg-blue-600' : 'bg-gray-300'
                        } peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-400 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                          isChecked ? 'after:translate-x-full after:border-white' : ''
                        }`}
                      ></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {cat.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm"
          >
            設定を保存
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
